import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, Download, ChevronDown, ChevronUp, X, AlertTriangle, Check, Clock, FileText } from 'lucide-react'
import { spkService } from '../services/spkService'
import { fileService } from '../services/fileService'
import { useAuth } from '../context/AuthContext'

function SPKFilesPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const [spk, setSpk] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploadSlot, setUploadSlot] = useState(null) // { fileType, schoolCode }

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await spkService.getById(id)
      if (res.success) {
        setSpk(res.data.spk)
      }
    } catch (err) {
      console.error('Error loading SPK:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!spk) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">SPK tidak ditemukan</p>
        <button onClick={() => navigate('/spk')} className="mt-4 text-blue-600 hover:underline">
          Kembali ke SPK
        </button>
      </div>
    )
  }

  const customSchools = spk.customSchools || []
  const files = spk.files || []

  // Build all expected file slots
  const slots = []

  // ISI
  slots.push({ type: 'isi', label: 'File ISI', schoolCode: null, schoolName: null })

  // Cover reguler
  slots.push({ type: 'cover', label: 'File Cover', schoolCode: null, schoolName: null })

  // Custom schools
  customSchools.forEach(cs => {
    slots.push({
      type: 'cover_custom',
      label: `Cover Custom - ${cs.school_name || cs.school_code}`,
      schoolCode: cs.school_code,
      schoolName: cs.school_name,
    })
    if (cs.custom_type === 'cover_sisipan') {
      slots.push({
        type: 'sisipan',
        label: `Sisipan - ${cs.school_name || cs.school_code}`,
        schoolCode: cs.school_code,
        schoolName: cs.school_name,
      })
    }
  })

  // Find file for a slot
  const getFileForSlot = (type, schoolCode) => {
    return files.find(f =>
      f.file_type === type &&
      (schoolCode ? f.school_code === schoolCode : !f.school_code)
    ) || null
  }

  // Count stats
  const totalSlots = slots.length
  const validatedCount = slots.filter(s => getFileForSlot(s.type, s.schoolCode)?.status === 'validated').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/spk')}
          className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Kembali ke SPK</span>
        </button>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800">{spk.judul}</h1>
              <p className="text-gray-500 text-sm mt-1">
                {spk.id} &bull; {spk.kode} &bull; {spk.qty?.toLocaleString()} eks &bull; Deadline: {new Date(spk.deadline).toLocaleDateString('id-ID')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs font-bold rounded ${
                { urgent: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', normal: 'bg-blue-100 text-blue-700', low: 'bg-gray-100 text-gray-600' }[spk.priority] || 'bg-blue-100 text-blue-700'
              }`}>
                {spk.priority?.toUpperCase()}
              </span>
              <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
                {spk.status?.toUpperCase().replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Progress summary */}
          <div className="mt-3 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${validatedCount === totalSlots ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${totalSlots > 0 ? (validatedCount / totalSlots) * 100 : 0}%` }}
                />
              </div>
              <span className="text-sm text-gray-600">{validatedCount}/{totalSlots} file validated</span>
            </div>
          </div>
        </div>
      </div>

      {/* File Slots */}
      <div className="space-y-4">
        {slots.map((slot, idx) => (
          <FileSlotCard
            key={`${slot.type}-${slot.schoolCode || 'reguler'}`}
            slot={slot}
            file={getFileForSlot(slot.type, slot.schoolCode)}
            spk={spk}
            hasPermission={hasPermission}
            onUpload={() => setUploadSlot({ fileType: slot.type, schoolCode: slot.schoolCode })}
            onRefresh={loadData}
          />
        ))}
      </div>

      {/* Upload Modal */}
      {uploadSlot && (
        <UploadFileModal
          spk={spk}
          fileType={uploadSlot.fileType}
          schoolCode={uploadSlot.schoolCode}
          existingFile={getFileForSlot(uploadSlot.fileType, uploadSlot.schoolCode)}
          onClose={() => setUploadSlot(null)}
          onSuccess={() => { setUploadSlot(null); loadData() }}
        />
      )}
    </div>
  )
}

// File Slot Card
function FileSlotCard({ slot, file, spk, hasPermission, onUpload, onRefresh }) {
  const [showHistory, setShowHistory] = useState(false)
  const [versions, setVersions] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  const statusConfig = {
    validated: { bg: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700', icon: <Check className="w-4 h-4" />, label: 'Validated' },
    pending: { bg: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-4 h-4" />, label: 'Pending Review' },
    rejected: { bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', icon: <AlertTriangle className="w-4 h-4" />, label: 'Rejected' },
  }

  const config = file ? statusConfig[file.status] : null

  const loadHistory = async () => {
    if (!file || versions.length > 0) {
      setShowHistory(!showHistory)
      return
    }
    setLoadingHistory(true)
    try {
      const res = await fileService.getVersionHistory(file.id)
      if (res.success) {
        setVersions(res.data.versions || [])
      }
    } catch (err) {
      console.error('Error loading history:', err)
    } finally {
      setLoadingHistory(false)
      setShowHistory(true)
    }
  }

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await fileService.download(fileId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download error:', err)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const formatSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / 1048576).toFixed(1) + ' MB'
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border overflow-hidden ${config ? config.bg : ''}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{slot.label}</h3>
          <div className="flex items-center gap-2">
            {file && (
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                {config.icon}
                {config.label} v{file.version || 1}
              </span>
            )}
          </div>
        </div>

        {file ? (
          <div className="mt-3">
            {/* File info */}
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">{file.filename}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatSize(file.file_size)}
                  {file.pages ? ` | ${file.pages} hal` : ''}
                  {file.uploaded_by_name ? ` | ${file.uploaded_by_name}` : ''}
                  {` | ${formatDate(file.uploaded_at)}`}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <button
                  onClick={() => handleDownload(file.id, file.filename)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                {file.status === 'rejected' && hasPermission(['files', 'spk']) && (
                  <button
                    onClick={onUpload}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Revisi
                  </button>
                )}
              </div>
            </div>

            {/* Reject reason */}
            {file.status === 'rejected' && file.reject_reason && (
              <div className="mt-2 p-2 bg-red-100 rounded-lg">
                <p className="text-sm text-red-700">
                  <span className="font-medium">Alasan penolakan:</span> {file.reject_reason}
                </p>
              </div>
            )}

            {/* Version history toggle */}
            {file.version > 1 && (
              <button
                onClick={loadHistory}
                className="mt-2 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {loadingHistory ? 'Memuat...' : `Histori versi (${file.version} versi)`}
              </button>
            )}

            {/* Version history list */}
            {showHistory && versions.length > 0 && (
              <div className="mt-2 space-y-1 pl-4 border-l-2 border-gray-200">
                {versions.filter(v => v.id !== file.id).map(v => (
                  <div key={v.id} className="flex items-center justify-between py-1.5">
                    <div>
                      <span className="text-sm text-gray-600">v{v.version}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                        v.status === 'validated' ? 'bg-green-100 text-green-700' :
                        v.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {v.status}
                      </span>
                      {v.reject_reason && (
                        <span className="ml-2 text-xs text-red-500">- {v.reject_reason}</span>
                      )}
                      <span className="ml-2 text-xs text-gray-400">{formatDate(v.uploaded_at)}</span>
                    </div>
                    <button
                      onClick={() => handleDownload(v.id, v.filename)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* No file uploaded */
          <div className="mt-3">
            {hasPermission(['files', 'spk']) ? (
              <button
                onClick={onUpload}
                className="w-full flex items-center justify-center gap-2 py-6 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span className="font-medium">Upload File</span>
              </button>
            ) : (
              <div className="py-6 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                Belum ada file
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Upload Modal (moved from SPKListPage)
function UploadFileModal({ spk, fileType, schoolCode, existingFile, onClose, onSuccess }) {
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [pageCount, setPageCount] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const isRevision = existingFile?.status === 'rejected'

  const getFileTypeLabel = () => {
    if (fileType === 'isi') return 'ISI'
    if (fileType === 'cover') return 'COVER'
    if (fileType === 'cover_custom') return `COVER CUSTOM (${schoolCode})`
    if (fileType === 'sisipan') return `SISIPAN (${schoolCode})`
    return fileType.toUpperCase()
  }

  const needsPageCount = fileType === 'isi' || fileType === 'sisipan'

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Pilih file terlebih dahulu')
      return
    }
    if (needsPageCount && !pageCount) {
      setError('Jumlah halaman harus diisi')
      return
    }

    setUploading(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('spkId', spk.id)
      formData.append('fileType', fileType)
      if (schoolCode) formData.append('schoolCode', schoolCode)
      if (needsPageCount) formData.append('pageCount', pageCount)

      const response = await fileService.upload(formData)
      if (response.success) {
        onSuccess()
      } else {
        setError(response.message || 'Gagal upload')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal upload file')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            {isRevision ? 'Upload Revisi' : 'Upload File'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-gray-600">
            {spk.judul} - <span className="font-semibold">{getFileTypeLabel()}</span>
          </p>

          {isRevision && existingFile && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-700 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                File ditolak (v{existingFile.version || 1})
              </p>
              {existingFile.reject_reason && (
                <p className="text-sm text-red-600 mt-1">Alasan: {existingFile.reject_reason}</p>
              )}
            </div>
          )}

          {existingFile && existingFile.status === 'pending' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700">File sudah diupload dan menunggu review.</p>
            </div>
          )}
          {existingFile && existingFile.status === 'validated' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">File sudah divalidasi.</p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {(!existingFile || existingFile.status === 'rejected') && (
            <>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-blue-300 rounded-xl p-8 text-center cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <Upload className="w-10 h-10 text-blue-400 mx-auto mb-2" />
                {selectedFile ? (
                  <p className="text-blue-600 font-medium">{selectedFile.name}</p>
                ) : (
                  <>
                    <p className="text-blue-600 font-medium">Klik untuk pilih file</p>
                    <p className="text-gray-500 text-sm">PDF, JPG, PNG, TIFF</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => e.target.files[0] && setSelectedFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  className="hidden"
                />
              </div>

              {needsPageCount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Halaman *</label>
                  <input
                    type="number"
                    value={pageCount}
                    onChange={(e) => setPageCount(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Contoh: 256"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : isRevision ? 'Upload Revisi' : 'Upload'}
                </button>
                <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Batal
                </button>
              </div>
            </>
          )}

          {existingFile && (existingFile.status === 'pending' || existingFile.status === 'validated') && (
            <div className="flex justify-end pt-2">
              <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Tutup
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SPKFilesPage
